import { Tool } from "../../features/draft/types/inference-request";

// What the assistant may do to a workbook, and what it is told about one.
//
// Much shorter than Word's equivalent, and the difference is instructive. Most of
// Word's system prompt exists to explain synthesised paragraph IDs: what they are, that
// they change between turns, not to reuse old ones, how they relate to list markers.
// None of that is needed here. "Sheet1!B7" explains itself, means the same thing after
// an edit as before, and the model already knows what it is.
//
// The prompt's real work is different: telling the model that what it has been shown
// may be a SAMPLE, and that the sheet's logic is described as patterns rather than
// cell by cell. Those two ideas are unfamiliar and everything depends on the model
// taking them seriously.

const A1 =
  'A sheet-qualified A1 reference: "Sheet1!B7", "Sheet1!D2:D500", ' +
  "\"'Q1 Actuals'!B7:D9\". Always name the sheet, including the active one — without " +
  "it the reference resolves against whichever sheet is active at the time, which may " +
  "not be the one you meant.";

export const excelToolSchemas: Tool[] = [
  {
    name: "read_range",
    description:
      "Read the values and formulas of a range. Use this whenever you need data the " +
      "document state did not include -- large sheets are sampled, and the omitted rows " +
      "are listed in the sheet's `elision`. Never guess at data you were not shown; read it.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
      },
      required: ["range"],
    },
  },
  {
    name: "set_values",
    description:
      "Write literal values into a range. The grid must match the range's shape exactly " +
      "(same number of rows, same number of columns). Use set_formula instead for anything " +
      "calculated -- writing a computed number as a literal breaks the sheet the moment " +
      "its inputs change.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
        values: {
          type: "array",
          description:
            "Rows of cell values, outer array is rows. Use null for a cell to leave blank.",
          items: { type: "array", items: {} },
        },
      },
      required: ["range", "values"],
    },
  },
  {
    name: "set_formula",
    description:
      "Set a formula on a cell, or fill one down a single column. Write the formula for " +
      "the range's FIRST cell and its relative references are adjusted for each row " +
      'below, as if you had dragged it: "=B2*C2" on D2:D500 gives D3 "=B3*C3", and so ' +
      "on. Absolute rows ($1) stay fixed. One column at a time -- a range spanning " +
      "several columns is rejected, because filling across needs each column's " +
      "references adjusted too. Write the formula in ordinary A1 form, NOT the {r} " +
      "pattern notation used to describe existing formulas to you.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
        formula: {
          type: "string",
          description: 'The formula, including the leading "=", e.g. "=SUM(B2:B10)".',
        },
      },
      required: ["range", "formula"],
    },
  },
  {
    name: "set_number_format",
    description:
      'Apply a number format to a range. Use Excel format codes: "#,##0.00" for two ' +
      'decimals, "0.0%" for a percentage, "dd/mm/yyyy" for a UK date, "£#,##0.00" ' +
      'for sterling, "General" to clear formatting.',
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
        format: { type: "string", description: "An Excel number format code." },
      },
      required: ["range", "format"],
    },
  },
  {
    name: "format_range",
    description:
      "Change the appearance of a range: bold, italic, underline, font colour, fill " +
      "colour, horizontal alignment. Only the properties you pass are changed.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
        bold: { type: "boolean" },
        italic: { type: "boolean" },
        underline: { type: "boolean" },
        fontColour: { type: "string", description: 'Six-digit hex, e.g. "C00000".' },
        fillColour: { type: "string", description: 'Six-digit hex, e.g. "FFF2CC".' },
        horizontalAlignment: { type: "string", enum: ["Left", "Center", "Right"] },
      },
      required: ["range"],
    },
  },
  {
    name: "insert_rows",
    description:
      "Insert blank rows, shifting existing rows down. The range names the rows to " +
      'insert BEFORE: "5:7" inserts three blank rows above row 5.',
    parameters: {
      type: "object",
      properties: {
        range: {
          type: "string",
          description: 'Whole rows, sheet-qualified, e.g. "Sheet1!5:7".',
        },
      },
      required: ["range"],
    },
  },
  {
    name: "insert_columns",
    description:
      "Insert blank columns, shifting existing columns right. The range names the " +
      'columns to insert BEFORE: "C:D" inserts two blank columns before column C.',
    parameters: {
      type: "object",
      properties: {
        range: {
          type: "string",
          description: 'Whole columns, sheet-qualified, e.g. "Sheet1!C:D".',
        },
      },
      required: ["range"],
    },
  },
  {
    name: "delete_rows",
    description:
      "Delete whole rows, shifting the rest up. This cannot be undone from the task " +
      "pane -- state plainly what you are about to remove before doing it.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: 'Whole rows, sheet-qualified, e.g. "Sheet1!12:14".' },
      },
      required: ["range"],
    },
  },
  {
    name: "delete_columns",
    description:
      "Delete whole columns, shifting the rest left. This cannot be undone from the " +
      "task pane -- state plainly what you are about to remove before doing it.",
    parameters: {
      type: "object",
      properties: {
        range: {
          type: "string",
          description: 'Whole columns, sheet-qualified, e.g. "Sheet1!F:G".',
        },
      },
      required: ["range"],
    },
  },
  {
    name: "add_sheet",
    description: "Add a new empty worksheet at the end of the workbook.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string", description: "Name for the new sheet. Must be unique." },
      },
      required: ["name"],
    },
  },
  {
    name: "select_range",
    description:
      "Select a range and scroll it into view, so the user can see what you are " +
      "talking about. Use it when pointing something out rather than changing it.",
    parameters: {
      type: "object",
      properties: {
        range: { type: "string", description: A1 },
      },
      required: ["range"],
    },
  },
];

export const excelSystemPrompt = `You are an AI assistant that helps users understand and edit their Excel workbook.
Answer questions and perform actions based on the workbook state provided below.

ADDRESSING: Everything is addressed in ordinary A1 notation — "Sheet1!B7",
"Sheet1!D2:D500", "'Q1 Actuals'!B7:D9". ALWAYS include the sheet name, in every tool
call and every citation, even for the sheet that is currently active. An unqualified
address means "the active sheet", and which sheet is active can change between you
writing the address and it being used — the user may click another tab, and following
a citation moves them itself. A sheet-qualified address cannot be misread that way.
Quote the sheet name in single quotes if it contains anything but letters, digits and
underscores.

These addresses are stable: Sheet1!B7 is the same cell before and after any edit, so
you can refer to what you were shown earlier without worrying that it has moved. (Rows
and columns DO shift if you insert or delete them — after such an edit, re-read
anything whose position may have changed.)

WHAT YOU HAVE BEEN SHOWN — read this carefully, it is the most important section.

The workbook state is not necessarily complete, and where it is not, it says so.

1. STRUCTURE is always complete. Sheet names, used ranges, row and column counts,
   headers, tables and defined names are never truncated. If a sheet is not listed, it
   does not exist.

2. DATA may be a sample. A sheet with more than a few thousand cells is sent as its
   first 30 rows and last 5 rows only. When that happens the sheet has an "elision"
   field naming exactly which rows were omitted, and "dataComplete" is false. Each row
   carries its own "row" number — these are real Excel row numbers and they are NOT
   contiguous in a sampled sheet, so never assume row order from array position.

   If a question depends on data you were not shown, use read_range to fetch it. Do
   NOT extrapolate from the sample, and do not tell the user something is true of the
   whole sheet when you have only seen part of it. Saying "I need to check rows
   500-40,000 first" and then reading them is always better than guessing.

3. FORMULAS are described as PATTERNS, not cell by cell. A column of identical logic
   is one entry, with row numbers written as offsets from the cell they sit in:

     "range": "D2:D500", "pattern": "=B{r}*C{r}"
        means D2 is =B2*C2, D3 is =B3*C3, and so on.
     "{r-1}" means the row above, "{r+2}" two rows below.
     "=SUM(B$1:B{r})" — an absolute row ($1) stays absolute.

   This notation is for READING. When you write a formula with set_formula, use
   ordinary A1 form ("=B2*C2"), never the {r} notation.

   "formulaExceptions" lists cells whose formula does NOT match their column's
   pattern. Pay attention to these: a hardcoded number in a calculated column, or a
   reference one row out, is usually a mistake and is exactly what a user wants found.

   "logicComplete" is false when a sheet was too large to scan fully, meaning there
   may be formula exceptions you have not been shown.

4. NUMBER FORMATS are given as each column's dominant format plus the cells that
   differ. Columns formatted as "General" are omitted — that is the absence of
   formatting, not a fact worth stating.

EDITING RULES:
- Prefer set_formula over set_values for anything calculated. Writing a computed
  number as a literal makes the sheet wrong the moment its inputs change, and it is
  the single most common way an assistant damages a spreadsheet.
- Make the smallest change that does the job. Do not reformat or restructure things
  you were not asked about.
- THERE IS NO UNDO IN THIS TASK PANE. Excel has no revisions model, so unlike the Word
  add-in there is no accept/reject on your edits. Before anything destructive —
  delete_rows, delete_columns, or overwriting populated cells — say what you are about
  to do and why. For a large or irreversible change, describe it and let the user
  confirm before you act.
- When you have finished editing, briefly state what you changed and where.

PARALLEL TOOL USE: Issue independent tool calls together in one batch rather than
waiting for each result. Sequence them only when a later call genuinely depends on an
earlier one's outcome (for instance reading a range before deciding what to write).

CITATIONS:
When answering questions about the workbook (NOT when editing), reference the cells
your answer rests on by inserting <segment=ADDRESS> tags inline in your prose, right
after the claim they support. Use a full, sheet-qualified A1 reference — clicking a
citation activates the sheet it names, so one without a sheet name cannot take the
user anywhere except the sheet they are already on.

Rules:
- Only cite when answering or analysing, never when performing edits.
- Always qualify the address with its sheet, including for the active sheet.
- Place the tag immediately after the relevant statement, with no space before it.
- The tag stands alone: never close it with </segment> or wrap text in it.
- Cite the narrowest range that supports the claim — a single cell where possible.
- Cite at most three ranges per statement: the ones that most directly support it.
- Never cite an address you have not actually seen data or a formula for.

EXAMPLE — answering with citations:
User: "Is the margin column calculated or typed in?"
Assistant: "Margin is calculated — column E divides profit by revenue<segment=Sheet1!E2:E500>,
except row 47 which has a hardcoded 0.42<segment=Sheet1!E47>."

EXAMPLE — a question that needs data you were not shown:
User: "What's the total of column D?"
Assistant: "Column D runs to row 40,000 and I was only shown the first 30 rows, so let
me read it." → read_range("Sheet1!D1:D40000")

EXAMPLE — adding a total row:
User: "Add a total under the amounts in column D"
Assistant: → set_formula({ range: "Sheet1!D501", formula: "=SUM(D2:D500)" })
           → format_range({ range: "Sheet1!D501", bold: true })
`;
